// The UCI codec: what an engine says, decoded; what it is told, encoded.
// Pure functions over lines of text, shared by every provider.
import type { EngineLine, EngineOption, EngineScore } from './types.ts';

/** An `info` line with a principal variation, decoded. Lines without a pv (currmove, string) decode to undefined. */
export interface UciInfo {
  depth: number;
  multipv: number;
  score: EngineScore;
  pv: string[];
  nodes?: number;
  nps?: number;
  time?: number;
}

export type UciMessage =
  | { kind: 'id'; field: 'name' | 'author'; value: string }
  | { kind: 'option'; option: EngineOption }
  | { kind: 'uciok' }
  | { kind: 'readyok' }
  | { kind: 'info'; info: UciInfo }
  | { kind: 'bestmove'; move: string | undefined; ponder?: string }
  | { kind: 'other'; line: string };

export function parseUci(raw: string): UciMessage {
  const line = raw.trim();
  const tokens = line.split(/\s+/);
  switch (tokens[0]) {
    case 'uciok':
      return { kind: 'uciok' };
    case 'readyok':
      return { kind: 'readyok' };
    case 'id': {
      const field = tokens[1] === 'author' ? 'author' : 'name';
      return { kind: 'id', field, value: tokens.slice(2).join(' ') };
    }
    case 'bestmove':
      return {
        kind: 'bestmove',
        move: tokens[1] && tokens[1] !== '(none)' ? tokens[1] : undefined,
        ponder: tokens[2] === 'ponder' ? tokens[3] : undefined,
      };
    case 'option':
      return { kind: 'option', option: parseOption(tokens) };
    case 'info': {
      const info = parseInfo(tokens);
      return info ? { kind: 'info', info } : { kind: 'other', line };
    }
    default:
      return { kind: 'other', line };
  }
}

function parseInfo(tokens: string[]): UciInfo | undefined {
  let depth: number | undefined;
  let multipv = 1;
  let score: EngineScore | undefined;
  let pv: string[] | undefined;
  let nodes: number | undefined;
  let nps: number | undefined;
  let time: number | undefined;
  for (let i = 1; i < tokens.length; i++) {
    switch (tokens[i]) {
      case 'depth':
        depth = Number(tokens[++i]);
        break;
      case 'multipv':
        multipv = Number(tokens[++i]);
        break;
      case 'nodes':
        nodes = Number(tokens[++i]);
        break;
      case 'nps':
        nps = Number(tokens[++i]);
        break;
      case 'time':
        time = Number(tokens[++i]);
        break;
      case 'score': {
        const unit = tokens[++i];
        const value = Number(tokens[++i]);
        score = unit === 'mate' ? { mate: value } : { cp: value };
        if (tokens[i + 1] === 'lowerbound') {
          score.bound = 'lower';
          i++;
        } else if (tokens[i + 1] === 'upperbound') {
          score.bound = 'upper';
          i++;
        }
        break;
      }
      case 'pv':
        pv = tokens.slice(i + 1);
        i = tokens.length;
        break;
      case 'string':
        return undefined;
      default:
        // seldepth, hashfull, tbhits, currmove, currmovenumber: a value follows, skip it
        if (['seldepth', 'hashfull', 'tbhits', 'currmove', 'currmovenumber', 'cpuload'].includes(tokens[i])) i++;
    }
  }
  if (depth === undefined || !score || !pv || pv.length === 0) return undefined;
  return { depth, multipv, score, pv, nodes, nps, time };
}

function parseOption(tokens: string[]): EngineOption {
  const option: EngineOption = { name: '', type: 'string' };
  const nameParts: string[] = [];
  for (let i = 1; i < tokens.length; i++) {
    switch (tokens[i]) {
      case 'name':
        while (i + 1 < tokens.length && tokens[i + 1] !== 'type') nameParts.push(tokens[++i]);
        break;
      case 'type':
        option.type = tokens[++i] as EngineOption['type'];
        break;
      case 'default':
        option.default = tokens[++i] === '<empty>' ? '' : tokens[i];
        break;
      case 'min':
        option.min = Number(tokens[++i]);
        break;
      case 'max':
        option.max = Number(tokens[++i]);
        break;
      case 'var':
        (option.vars ??= []).push(tokens[++i]);
        break;
    }
  }
  option.name = nameParts.join(' ');
  return option;
}

/** An info line as one engine line. */
export function lineFromInfo(info: UciInfo): EngineLine {
  return { rank: info.multipv, depth: info.depth, score: info.score, pv: info.pv };
}

/** Commands, encoded. */
export const uci = {
  uci: () => 'uci',
  isready: () => 'isready',
  ucinewgame: () => 'ucinewgame',
  setoption: (name: string, value: string | number | boolean) => `setoption name ${name} value ${String(value)}`,
  position: (fen: string, moves: string[] = []) =>
    `position fen ${fen}${moves.length ? ` moves ${moves.join(' ')}` : ''}`,
  go: (opts: { depth?: number; moveTimeMs?: number }) => {
    const parts = ['go'];
    if (opts.depth !== undefined) parts.push('depth', String(opts.depth));
    if (opts.moveTimeMs !== undefined) parts.push('movetime', String(opts.moveTimeMs));
    if (parts.length === 1) parts.push('infinite');
    return parts.join(' ');
  },
  stop: () => 'stop',
  quit: () => 'quit',
};
